<template>
  <div class="card-offered">
    <div class="card-offered-main">
      <card-info
        :item="offered"
        :show-year="true"
      />
      <traceability-documents
        :has-mtr="Boolean(offered.has_mtr)"
        :has-cdf="Boolean(offered.has_cdf)"
      />
    </div>

    <div class="card-actions">
      <div class="values-box">
        <div class="total-block">
          <div class="action-title">
            {{ mountTranslate('total_value') }}
          </div>
          <div class="total-value">
            {{ formatMoney(offered.total_value_with_aditional) }}
          </div>
        </div>

        <div class="values-divider" />

        <div class="values-breakdown">
          <div class="value-item">
            <div class="text-unit-value">
              {{ mountTranslate('central_custody_fee_value') }}
            </div>
            <div class="value-amount">
              {{ formatMoney(offered.central_custody_fee_value) }}
            </div>
          </div>
          <div class="values-breakdown-divider" />
          <div class="value-item">
            <div class="text-unit-value">
              {{ mountTranslate('credit_value') }}
            </div>
            <div class="value-amount">
              {{ formatMoney(offered.unit_value_with_aditional) }}
            </div>
          </div>
        </div>
      </div>

      <div class="card-actions-bottom">
        <div
          v-if="showActions"
          class="button-actions"
        >
          <button
            type="button"
            class="add-cart-button"
            @click="addOfferToCart(false)"
          >
            <v-icon
              size="20"
              color="primary"
            >
              mdi-cart-outline
            </v-icon>
            {{ mountTranslate('buttons.add_cart') }}
          </button>
          <button
            type="button"
            class="buy-button"
            @click="addOfferToCart(true)"
          >
            <v-icon
              size="20"
              color="white"
            >
              mdi-tag-outline
            </v-icon>
            {{ mountTranslate('buttons.buy_credit') }}
          </button>
        </div>

        <div class="secure-transaction">
          <v-icon
            size="16"
            color="primary"
          >
            mdi-shield-check
          </v-icon>
          <span>{{ mountTranslate('secure_transaction') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import formatMoney from '@/mixins/formatMoney';
import CardInfo from '@/components/cards/offered/CardInfo.vue';
import TraceabilityDocuments from '@/components/cards/offered/TraceabilityDocuments.vue';

export default {
  name: 'CardOffered',
  components: {
    CardInfo,
    TraceabilityDocuments,
  },
  mixins: [formatMoney],
  props: {
    offered: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters([
      'getCartItem',
      'getBusiness',
    ]),
    isRecEntity() {
      return this.getBusiness.entity_flag;
    },
    showActions() {
      return !this.isRecEntity || !this.offered.is_reoffered;
    },
  },
  methods: {
    ...mapActions([
      'addToCart',
    ]),
    mountTranslate(path, value) {
      return this.$t(['components.offered.card_offered', path].join('.'), {
        value,
      });
    },

    addOfferToCart(directPurchase) {
      this.addToCart({
        credit_offered_id: this.offered.id,
        redirectTo: directPurchase ? 'checkout' : 'cart',
      }).then((result) => {
        if (result && result.success && result.redirectTo) {
          this.$router.push({ name: result.redirectTo });
        }
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.card-offered {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  background: $card_offer;
  border-radius: 10px;
  padding: 38px;

  .card-offered-main {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
    min-width: 0;
    max-width: calc(70% - 12px);
  }

  .card-actions {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 30%;
    flex-shrink: 0;
    align-self: stretch;
    border-left: 1px solid $border_color_divider;
    padding-left: 24px;
  }

  .values-box {
    background: $dashboard_background;
    border-radius: 12px;
    padding: 24px;
  }

  .action-title {
    font-size: 1em;
    color: $color_gray;
    margin-bottom: 10px;
  }

  .total-value {
    font-size: 1.85em;
    color: $color_dark;
    font-weight: 700;
    line-height: 1.2;
  }

  .values-divider {
    height: 1px;
    background: $border_color_divider;
    margin: 22px 0;
  }

  .values-breakdown {
    display: flex;
    align-items: stretch;
  }

  .values-breakdown-divider {
    width: 1px;
    background: $border_color_divider;
    margin: 0 16px;
    flex-shrink: 0;
  }

  .value-item {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  .text-unit-value {
    font-size: 0.875em;
    color: $color_gray;
    margin-bottom: 12px;
    line-height: 1.3;
  }

  .value-amount {
    margin-top: auto;
    font-size: 1.15em;
    color: $color_dark;
    font-weight: 700;
  }

  .card-actions-bottom {
    display: flex;
    flex-direction: column;
    margin-top: auto;
    padding-top: 32px;
  }

  .button-actions {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .add-cart-button,
  .buy-button {
    width: 100%;
    height: 52px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 999px;
    font-size: 1em;
    font-weight: 700;
    cursor: pointer;
  }

  .add-cart-button {
    background: transparent;
    border: 2px solid $color_primary;
    color: $color_primary;
  }

  .buy-button {
    border: 0;
    background: $color_primary;
    color: $color_white;
  }

  .secure-transaction {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 20px;
    font-size: 0.75em;
    color: $color_gray;
  }
}
</style>
